import React, { useEffect } from 'react';
import { CheckCircle2, AlertTriangle, Info, X } from 'lucide-react';

export type MeetingToastTone = 'success' | 'error' | 'info';

export interface MeetingToastState {
  message: string;
  tone?: MeetingToastTone;
}

interface MeetingToastProps {
  toast: MeetingToastState | null;
  onDismiss: () => void;
  durationMs?: number;
}

const toneStyles: Record<MeetingToastTone, string> = {
  success: 'border-emerald-500/40 bg-slate-900/95 text-emerald-100',
  error: 'border-red-500/40 bg-slate-900/95 text-red-100',
  info: 'border-purple-500/40 bg-slate-900/95 text-white',
};

const toneIcon = {
  success: CheckCircle2,
  error: AlertTriangle,
  info: Info,
};

/** Engellemeyen üst bildirim — kayıt vb. için. */
export const MeetingToast: React.FC<MeetingToastProps> = ({
  toast,
  onDismiss,
  durationMs = 4500,
}) => {
  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(onDismiss, durationMs);
    return () => window.clearTimeout(timer);
  }, [toast, onDismiss, durationMs]);

  if (!toast) return null;

  const tone = toast.tone ?? 'info';
  const Icon = toneIcon[tone];

  return (
    <div className="fixed top-16 left-1/2 -translate-x-1/2 z-[70] max-w-[92vw] w-full sm:w-auto px-3 pointer-events-none">
      <div
        className={`pointer-events-auto flex items-start gap-3 rounded-xl border px-4 py-3 shadow-2xl backdrop-blur-md ${toneStyles[tone]}`}
        role="status"
      >
        <Icon className="w-5 h-5 shrink-0 mt-0.5 opacity-90" />
        <p className="text-sm font-medium leading-snug flex-1 min-w-0">{toast.message}</p>
        <button
          type="button"
          onClick={onDismiss}
          className="shrink-0 p-0.5 rounded-lg hover:bg-white/10 transition"
          aria-label="Kapat"
        >
          <X className="w-4 h-4 opacity-70" />
        </button>
      </div>
    </div>
  );
};

export interface MeetingNoticeState {
  title: string;
  message: string;
  confirmLabel?: string;
}

interface MeetingNoticeModalProps {
  notice: MeetingNoticeState | null;
  onConfirm: () => void;
}

/** Tarayıcı alert yerine uygulama içi onay modalı. */
export const MeetingNoticeModal: React.FC<MeetingNoticeModalProps> = ({
  notice,
  onConfirm,
}) => {
  if (!notice) return null;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[80] p-4">
      <div className="bg-slate-800/95 rounded-2xl border border-white/10 p-6 max-w-md w-full shadow-2xl ring-1 ring-purple-500/20 text-center">
        <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-purple-600/20 flex items-center justify-center">
          <Info className="w-7 h-7 text-purple-300" />
        </div>
        <h3 className="text-xl font-bold text-white mb-2">{notice.title}</h3>
        <p className="text-gray-400 text-sm mb-6 leading-relaxed">{notice.message}</p>
        <button
          type="button"
          onClick={onConfirm}
          className="w-full px-4 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white rounded-xl font-medium transition shadow-lg shadow-purple-900/30"
        >
          {notice.confirmLabel ?? 'Tamam'}
        </button>
      </div>
    </div>
  );
};
